/**
 * useBlogList Hook
 * 
 * Custom React hook for fetching and managing blog post lists.
 * Provides loading states, error handling, and data caching.
 */

import { useState, useEffect, useCallback } from 'react';
import { blogService } from '../services/blogService';
import type { BlogPost, BlogQueryOptions } from '../types/blog.types';
import { ApiError } from '../services/apiClient';

/**
 * Hook return type
 */
export interface UseBlogListReturn {
  /** Array of blog posts */
  blogs: BlogPost[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Function to refetch blog list */
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching blog list
 */
export const useBlogList = (
  options: BlogQueryOptions = {}
): UseBlogListReturn => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const optionsKey = JSON.stringify(options);

  const fetchBlogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      const data = await blogService.getBlogList(options);
      setBlogs(data);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Failed to fetch blog posts';
      setError(errorMessage);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [optionsKey]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  return {
    blogs,
    isLoading,
    error,
    refetch: fetchBlogs,
  };
};

